import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { initRedditPixel, redditPixelEnabled } from './reddit';
import { adTrackingAllowed } from './adRegion';

/**
 * The only pages the pixel may load on: where the ads land, and the sign-up page where
 * the conversion fires. (#812)
 *
 * Deliberately narrow. The help centre, /login, /tos and /privacy are public but are
 * mostly read by existing customers, and the pixel is for acquisition. /login matters
 * most: ProtectedRoute sends a customer whose web session has expired from /books to
 * /login, so allowing it would load an ad tracker for a customer on their way back into
 * the app, where it would stay for the whole session.
 *
 * An allowlist, because the two mistakes are not equal. Leaving a page off costs some
 * attribution; letting a new route in by default could load an ad tracker over
 * someone's manuscript. The privacy policy names exactly these pages.
 */
export const MARKETING_PATHS = new Set([
  '/',
  '/features',
  '/features/assistant',
  '/compare',
  '/november',
  '/pricing',
  '/register',
]);

export const isMarketingPath = (pathname: string): boolean => MARKETING_PATHS.has(pathname);

/**
 * Loads the Reddit pixel when, and only when, all of these hold:
 *
 *   - a pixel ID is configured at build time;
 *   - the visitor is signed out. "/" is the app's home for a signed-in customer, and
 *     the pixel is for acquisition, not for watching customers. Waiting for a known
 *     "unauthenticated" also means it never loads while sign-in is still resolving;
 *   - the page is a public marketing page;
 *   - the visitor is in the US and their browser sends no Global Privacy Control.
 *
 * Replaces the unconditional initRedditPixel() that ran at boot in index.tsx, which
 * would have loaded it on every route for every visitor the moment the ID was set,
 * contradicting the privacy policy. Renders nothing.
 */
const AdPixelGate = ({ signedOut }: { signedOut: boolean }) => {
  const { pathname } = useLocation();
  const eligible = redditPixelEnabled && signedOut && isMarketingPath(pathname);

  useEffect(() => {
    if (eligible && adTrackingAllowed()) initRedditPixel();
  }, [eligible]);

  return null;
};

export default AdPixelGate;
